/* ─────────────────────────────────────────────────────────────
   The building's background traffic.

   A real access panel never sits still: residents badge in, a courier
   props the dock, a fob that expired last week is tried at the pool.
   Each tick here returns the next thing that happens, built from the
   real records (an actual suspended credential is the one denied, a
   door already propped is the one that closes), so the feed reads as
   this building rather than as noise.

   Pure: it returns writes for the screen to apply and changes nothing.
   ───────────────────────────────────────────────────────────── */

import { codeStatus, elapsed, scheduleFor, viaOf } from './model.js';

const pick = list => list[Math.floor(Math.random() * list.length)];
const between = (lo, hi) => lo + Math.random() * (hi - lo);

const HELD_CANDIDATES = ['dock', 'service', 'package', 'garage-vest', 'pool'];
const FORCED_CANDIDATES = ['stair-b', 'service'];
const VISITOR_DOORS = ['main', 'garage-gate'];
const LOCKDOWN_DOORS = ['main', 'lobby-inner', 'garage-gate', 'garage-vest'];

const HELD_REASON = {
    dock: 'Delivery in progress',
    service: 'Propped by vendor',
    package: 'Courier drop-off',
    'garage-vest': 'Moving cart in doorway',
    pool: 'Gate not latching'
};

/* `ops` is a list of { event, changes } for logAccess and, for a
   one-time visitor code, { code: id, codeChanges }. */
export function simulate({ doors, credentials, guestCodes, lockdown, schedules }, now = Date.now()) {
    const ops = [];
    const usable = doors.filter(d => d.status === 'ok' && !(d.pulseUntil > now));
    const active = credentials.filter(c => c.status === 'Active');

    /* Propped and alarmed doors close on their own after a while, and
       that is the only news on a tick it happens. */
    for (const d of doors) {
        if (d.status === 'held' && now - d.since > (d.holdFor || 40000)) {
            ops.push({
                event: { door: d.id, kind: 'alarm', tone: 'ok', label: 'Cleared', title: 'Door closed', who: `Held open ${elapsed(now - d.since)}`, via: 'Door contact' },
                changes: { status: 'ok', since: 0, holdFor: 0 }
            });
        }
        if (d.status === 'forced' && now - d.since > (d.holdFor || 70000)) {
            ops.push({
                event: { door: d.id, kind: 'alarm', tone: 'ok', label: 'Cleared', title: 'Alarm cleared, door secured', who: 'Marcus Reed, security', via: 'Guard tour' },
                changes: { status: 'ok', since: 0, holdFor: 0 }
            });
        }
    }
    if (ops.length) return ops;

    const deny = (credential, door, note) => ({
        event: {
            door: door.id, kind: 'denied', tone: 'alert', label: 'Denied', title: 'Access denied',
            who: credential ? credential.name : 'Unknown credential',
            via: credential ? viaOf(credential) : `Fob ${Math.floor(9000 + Math.random() * 999)}`,
            unit: credential && credential.type === 'Resident' ? credential.unit : undefined,
            note
        }
    });

    const grant = (credential, door) => {
        const schedule = scheduleFor(door, schedules);
        if (credential.type !== 'Staff' && schedule && schedule.kind === 'hours' && !schedule.enabled) {
            return deny(credential, door, 'Amenity closed, schedule off');
        }
        return {
            event: {
                door: door.id, kind: 'granted', tone: 'ok', label: 'Granted', title: 'Access granted',
                who: credential.name, via: viaOf(credential), unit: credential.type === 'Resident' ? credential.unit : undefined
            }
        };
    };

    /* During a lockdown residents are turned away and only security
       moves through the building. */
    if (lockdown) {
        const residents = active.filter(c => c.type === 'Resident');
        const doorsOut = usable.filter(d => LOCKDOWN_DOORS.includes(d.id));
        if (Math.random() < 0.3 || !residents.length || !doorsOut.length) {
            const guard = active.find(c => c.unit === 'Security') || active.find(c => c.type === 'Staff');
            const door = pick(usable);
            if (!guard || !door) return ops;
            return [{
                event: {
                    door: door.id, kind: 'granted', tone: 'info', label: 'Override', title: 'Security override',
                    who: guard.name, via: viaOf(guard), note: 'Lockdown patrol'
                }
            }];
        }
        return [deny(pick(residents), pick(doorsOut), 'Lockdown in effect')];
    }

    const roll = Math.random();
    const held = doors.filter(d => d.status === 'held').length;

    /* A resident, or now and then a staff member, badges in at a door
       their groups open. */
    if (roll < 0.5) {
        const who = Math.random() < 0.78 ? active.filter(c => c.type === 'Resident') : active.filter(c => c.type !== 'Resident');
        const credential = pick(who.length ? who : active);
        if (!credential) return ops;
        const theirs = usable.filter(d => credential.groups.includes(d.group));
        const door = pick(theirs.length ? theirs : usable);
        return door ? [grant(credential, door)] : ops;
    }

    /* A guest of another unit keys in their code at the front. */
    if (roll < 0.62) {
        const codes = guestCodes.filter(c => codeStatus(c) === 'Active' && c.unit !== '1204');
        const door = usable.find(d => d.id === pick(VISITOR_DOORS)) || usable[0];
        if (!door) return ops;
        const code = codes.length ? pick(codes) : null;
        const value = code ? code.code : String(Math.floor(100000 + Math.random() * 900000));
        const op = {
            event: {
                door: door.id, kind: 'granted', tone: 'ok', label: 'Visitor', title: 'Visitor code used',
                who: code ? code.guest : pick(['DoorDash courier', 'Amazon delivery', 'Uber Eats courier', 'Guest of 1110']),
                via: `Visitor code ${value}`, guestOf: code ? code.unit : undefined
            }
        };
        return code && code.uses === 'Once'
            ? [op, { code: code.id, codeChanges: { status: 'Used', usedAt: new Date(now).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) } }]
            : [op];
    }

    /* Someone is turned away, for a reason the records can back up. */
    if (roll < 0.78) {
        const lapsed = credentials.filter(c => c.status !== 'Active');
        const options = [];
        if (lapsed.length) {
            const c = pick(lapsed);
            const door = pick(usable.filter(d => c.groups.includes(d.group))) || pick(usable);
            const why = { Expired: 'Expired credential', Suspended: 'Suspended credential', Lost: 'Fob reported lost' }[c.status] || 'Credential not active';
            if (door) options.push(deny(c, door, why));
        }
        const resident = pick(active.filter(c => c.type === 'Resident'));
        const backOfHouse = pick(usable.filter(d => d.area === 'Back of house'));
        if (resident && backOfHouse) options.push(deny(resident, backOfHouse, 'No access to this door'));
        const front = usable.find(d => d.id === 'main');
        if (front) options.push(deny(null, front, 'Unregistered fob'));
        const packageRoom = usable.find(d => d.id === 'package');
        if (resident && packageRoom) options.push(deny(resident, packageRoom, 'PIN entered incorrectly'));
        return options.length ? [pick(options)] : ops;
    }

    /* A door is propped past its held-open limit. */
    if (roll < 0.9 && held < 2) {
        const door = pick(usable.filter(d => HELD_CANDIDATES.includes(d.id)));
        if (door) {
            return [{
                event: { door: door.id, kind: 'alarm', tone: 'warn', label: 'Held open', title: 'Door held open', who: HELD_REASON[door.id] || 'Open past its limit', via: 'Door contact' },
                changes: { status: 'held', since: now, holdFor: between(16000, 48000) }
            }];
        }
    }

    /* Rarely, a door opens with no credential at all. */
    if (roll < 0.93 && !doors.some(d => d.status === 'forced')) {
        const door = pick(usable.filter(d => FORCED_CANDIDATES.includes(d.id)));
        if (door) {
            return [{
                event: { door: door.id, kind: 'alarm', tone: 'alert', label: 'Forced', title: 'Door forced open', who: 'No credential presented', via: 'Door contact' },
                changes: { status: 'forced', since: now, holdFor: between(45000, 80000) }
            }];
        }
    }

    const staff = pick(active.filter(c => c.type === 'Staff'));
    const door = staff && pick(usable);
    return staff && door ? [grant(staff, door)] : ops;
}
